import type { EnrichedSynthNavData, GgeHoldingsRow } from '../api/client'
import type { HoldingsHybridRow } from '../utils/holdingsHybrid'

// PnlPanel "Today"/"Synthetic" column bridge for GGE — parallel of
// computeGoldTodaysMovePct in goldHoldingsRows.ts. The GGE enriched payload
// (`synth_nav:gge:enriched`, written by GgeHoldingsBackfillService) carries
// per-holding return_pct_gbp + weight + covered_weight_frac + daily_drag_frac
// but — unlike BGA/JH — does NOT emit a top-level `overnight_return_gbp`. This
// aggregates the per-holding rows into that number, splicing in the 60s
// holdings-hybrid publisher's live price when it covers a ticker (mirroring
// how GOLD bridges its own T-1-aligned payload against the live hybrid).
//
// The one shape difference from GOLD: GGE writes `weight` as a FRACTION
// (e.g. XOM ≈ 0.055, sum ≈ covered_weight_frac ≈ 1). GOLD writes `weight` as
// a percentage (e.g. NEM ≈ 4.7, sum > 1). GgeHoldingsBackfillService.cs:360
// writes `weight = weightFrac`; the redis payload confirms fractional units.
// So this aggregator uses `h.weight` directly, without the /100 that gold's
// version applies.
//
// GGE's payload does not currently include `stale_days` (no counterpart to
// gold's 07:45 UK daily-job freshness telemetry), so the >1-day stale gate is
// only applied if the field is present.
//
// 2026-10-31 (paolo: "GGE was up ~2.5K synthetic on Friday but today nothing
// shows"): GGE strikes at 12:00 UK, before the US open, so a US close on day c
// is first priced by the NAV struck on the next dealing day after c. Once the
// payload rolls prev_close onto a close that no struck NAV has priced yet
// (weekends and holidays, and weekday mornings from the 06:50 roll to the
// strike), measuring from it drops that session's move. Friday's +1.9% read
// ~0 all weekend, though Monday's NAV prices it. Such a holding measures from
// session_prev_close instead, the close the last struck NAV did price.
// Markets that close before 12:00 UK (.HK) are priced by the same-day NAV,
// so their close on the struck date is already in it.
const CLOSES_BEFORE_UK_NOON = /\.(HK|T|SS|SZ|AX|KS|TW|SI)$/

function isUnpricedClose(ticker: string, closeDate: string, lastStruckNavDate: string): boolean {
  return CLOSES_BEFORE_UK_NOON.test(ticker) ? closeDate > lastStruckNavDate : closeDate >= lastStruckNavDate
}

export function computeGgeTodaysMovePct(
  en: Pick<EnrichedSynthNavData, 'stale_days' | 'covered_weight_frac' | 'daily_drag_frac' | 'holdings_detail' | 'sell_pending_at_strike'> | null | undefined,
  hybridRowsByTicker?: Map<string, HoldingsHybridRow> | null,
  opts?: { sellPending?: boolean; lastStruckNavDate?: string | null },
): number | null {
  if (!en || !en.holdings_detail || en.holdings_detail.length === 0) return null
  const staleDays = en.stale_days
  if (staleDays != null && staleDays > 1) return null
  const covered = en.covered_weight_frac ?? null
  if (covered == null || covered === 0) return null
  const drag = en.daily_drag_frac ?? 0

  // See goldHoldingsRows.ts's computeGoldTodaysMovePct for the strike-freeze
  // rationale — same GGE 12:00 UK valuation point.
  const sellPending = opts?.sellPending ?? en.sell_pending_at_strike ?? false

  const lastStruck = opts?.lastStruckNavDate || null

  let weightedSum = 0
  let any = false
  for (const h of en.holdings_detail) {
    if (h.ticker == null || h.weight == null) continue
    const rolledPastStrike =
      lastStruck != null && h.session_close_date != null && h.session_prev_close != null &&
      h.session_prev_close > 0 && h.prev_close === h.session_close &&
      isUnpricedClose(h.ticker, h.session_close_date, lastStruck)
    const prevClose = rolledPastStrike ? h.session_prev_close! : h.prev_close ?? null
    const hybridRow = (hybridRowsByTicker?.get(h.ticker) as GgeHoldingsRow | undefined) ?? null
    const strikePrice = hybridRow?.strikePrice ?? null
    const hybridPrice = hybridRow?.price ?? null
    let r: number | null
    if (sellPending && strikePrice != null && prevClose != null && prevClose > 0) {
      r = strikePrice / prevClose - 1
    } else if (hybridPrice != null && prevClose != null && prevClose > 0) {
      r = hybridPrice / prevClose - 1
    } else if (
      h.latest_close_native != null && prevClose != null && prevClose > 0 &&
      h.latest_close_native !== prevClose
    ) {
      // Prefer the truly-latest native close (may be a session newer than
      // prev_close if the LSE/non-US calendar shifted alignment) over the
      // T-1 aligned return_pct_gbp — mirrors gold's fallback rationale.
      r = (h.latest_close_native as number) / (prevClose as number) - 1
    } else {
      r = h.return_pct_gbp ?? h.prev_return_pct ?? null
    }
    if (r == null) continue
    weightedSum += h.weight * r
    any = true
  }
  if (!any) return null
  return weightedSum / covered - drag
}
